<template>
  <BaseCard no-padding class="tw:lg:flex-1 tw:lg:flex tw:lg:flex-col">
    <div class="tw:relative tw:bg-white tw:w-full tw:rounded-xl tw:lg:flex-1 tw:lg:flex tw:lg:flex-col">
      <SetupStepper :steps="RATE_CHECK_STEPS" />

      <!-- Content area -->
      <div class="tw:flex-1 tw:flex tw:flex-col">
        <!-- Loading state -->
        <template v-if="status === 'loading'">
          <div class="tw:flex tw:flex-col tw:items-center tw:text-center tw:px-8 tw:py-12 tw:gap-6 tw:w-full">
            <div
              class="tw:rounded-full tw:p-4 tw:bg-green-500 tw:flex tw:items-center tw:justify-center tw:w-16 tw:h-16">
              <img
                src="@/assets/img/rate-connect/hotel-booking-round.svg"
                width="32"
                height="32"
                alt=""
                aria-hidden="true" />
            </div>
            <h2 class="tw:text-2xl tw:font-bold">{{ $t('rc_campaign_activated_header') }}</h2>
            <p>{{ $t('rc_rate_check_message', { ConnectivityProvider: providerName }) }}</p>
            <img src="@/assets/img/apps-loader.svg" alt="" aria-hidden="true" />
          </div>
        </template>

        <!-- Unavailable state -->
        <template v-else-if="status === 'unavailable'">
          <div
            class="tw:w-auto tw:px-4 tw:mx-auto tw:mt-[40px] tw:md:max-w-[650px] tw:lg:max-w-[500px] tw:mb-[60px] tw:md:my-[40px] tw:lg:my-[24px] tw:flex tw:flex-col tw:items-center tw:gap-6">
            <div
              class="tw:rounded-full tw:p-4 tw:bg-orange-500 tw:flex tw:items-center tw:justify-center tw:w-16 tw:h-16">
              <img src="@/assets/img/general/alert-icon.svg" width="28" height="28" alt="" aria-hidden="true" />
            </div>
            <h2 class="tw:text-2xl tw:font-bold tw:text-center">
              {{ $t('rc_rate_check_cp_header', { ConnectivityProvider: providerName }) }}
            </h2>
            <RateCheckIssueContent
              :providerName="providerName"
              :partnerId="partnerId"
              :fallbackKey="RC_RATE_CHECK_DEFAULT_STEPS_KEY"
              :contactUsUrl="contactUsUrl"
              variant="stepper" />
          </div>
        </template>

        <!-- Unknown state -->
        <template v-else-if="status === 'unknown'">
          <div
            class="tw:w-auto tw:px-4 tw:mx-auto tw:mt-[40px] tw:md:max-w-[650px] tw:lg:max-w-[500px] tw:mb-[60px] tw:md:my-[40px] tw:lg:my-[24px] tw:flex tw:flex-col tw:items-center tw:gap-6">
            <div
              class="tw:rounded-full tw:p-4 tw:bg-orange-500 tw:flex tw:items-center tw:justify-center tw:w-16 tw:h-16">
              <img src="@/assets/img/general/alert-icon.svg" width="28" height="28" alt="" aria-hidden="true" />
            </div>
            <h2 class="tw:text-2xl tw:font-bold">{{ $t('rc_rate_check_fail_header') }}</h2>
            <RateCheckIssueContent
              :partnerId="null"
              :fallbackKey="RC_RATE_CHECK_FAIL_STEPS_KEY"
              :providerName="providerName"
              subtitleKey="rc_rate_check_fail_subtext"
              variant="stepper" />
          </div>
        </template>
      </div>

      <!-- Footer — hidden while loading -->
      <div
        v-if="status !== 'loading'"
        class="tw:border-t tw:border-gray-300 tw:px-6 tw:py-4 tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:gap-y-4">
        <GhostButton
          v-if="status === 'unavailable'"
          class="tw:flex tw:items-center tw:gap-2"
          @click="$emit('contactUs')">
          <PaperEditIcon class="tw:size-4" />
          {{ $t('rc_contact_us_cta') }}
        </GhostButton>
        <div v-else />
        <MainButton @click="$emit('continue')">{{ ctaLabel }}</MainButton>
      </div>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseCard from '@/components/dashboard/BaseCard.vue'
import SetupStepper from '@/components/rateConnect/setup/SetupStepper.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import PaperEditIcon from '@/components/icons/PaperEditIcon.svg?component'
import type { RateCheckDisplayStatus } from '@/components/rateConnect/rateCheck/useRateCheck'
import RateCheckIssueContent from '@/components/rateConnect/rateCheck/RateCheckIssueContent.vue'
import { getRcSetupSteps } from '@/constants/rcSetupSteps'
import { RC_RATE_CHECK_FAIL_STEPS_KEY, RC_RATE_CHECK_DEFAULT_STEPS_KEY } from '@/constants/rcProviderContent'

defineProps<{
  status: RateCheckDisplayStatus
  providerName: string
  partnerId?: number | null
  contactUsUrl: string
}>()

defineEmits<{ continue: []; contactUs: [] }>()

const { t } = useI18n()

const RATE_CHECK_STEPS = getRcSetupSteps(4)

const ctaLabel = computed(() => t('rc_rate_check_continue_cta'))
</script>
